import { z } from "zod";

import {
  breakerKindSchema,
  dutyBasisSchema,
  selectivityKindSchema,
  type BreakerKind,
  type DutyBasis,
  type SelectivityKind,
} from "../services/breakerSelectivityContract";
import { jurisdictionProfileSchema } from "../services/cableContract";
import type { ValidationLabels } from "../utils/validationMessages";
import {
  CASE_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  FAULT_TYPE_LABELS,
  type FaultLevelSource,
} from "./starterCoordinationDraft";

// Draft model of the LV breaker duty and selectivity form (EOS-05b, A21): what
// the user is typing, kept apart from the components so the payload rules are
// tested without a browser.
//
// Every value is held as the string the user typed. The fault level comes from
// one of two places (A21 (a)): a stored EOS-04 fault run of the active revision
// (LINKED, the default) or a typed value (TYPED, which the engine reports as
// REVIEW_REQUIRED). The payload carries only the chosen source; a blank typed
// peak ip is left out, so the engine skips the making check and says so. A
// blank Ics, selectivity limit or cascading limit is left OUT of the payload:
// the schema then applies the backend's own rules (Ics for a SERVICE basis, a
// limit for a PARTIAL declaration). Every other device input is required, so a
// blank one is sent as "" for the schema to report by its on-screen label; a
// choice the user has not made ("" = Choose...) is left out so the schema
// reports it as required.
//
// Outgoing devices are separate rows with stable ids, so adding or removing one
// never disturbs the others; the ids never reach the backend. The incomer is one
// device, not a row.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const BREAKER_SELECTIVITY_DRAFT_MODULE = "breaker-selectivity";

// The fault level source, its words and the linked run's case and fault type words
// are the same as the starter coordination form's, so they are shared.
export { CASE_LABELS, FAULT_LEVEL_SOURCE_LABELS, FAULT_LEVEL_SOURCES, FAULT_TYPE_LABELS };
export type { FaultLevelSource };

/** Whether selectivity with the incomer is required, on screen: unchosen, yes or no. */
export const SELECTIVITY_REQUIRED_CHOICES = ["yes", "no"] as const;

export type SelectivityRequiredChoice = (typeof SELECTIVITY_REQUIRED_CHOICES)[number];

export type BreakerDeviceDraft = {
  code: string;
  kind: string;
  ratedCurrentA: string;
  ratedOperationalVoltageV: string;
  ultimateBreakingCapacityKa: string;
  serviceBreakingCapacityKa: string;
  makingCapacityKa: string;
  dutyBasis: string;
};

export type OutgoingDeviceDraft = BreakerDeviceDraft & {
  id: string;
  selectivityRequired: string;
  declaredSelectivityKind: string;
  selectivityLimitKa: string;
  cascadingLimitKa: string;
};

export type BreakerSelectivityDraft = {
  code: string;
  name: string;
  busCode: string;
  faultLevelSource: FaultLevelSource;
  faultRunId: string;
  typedProspectiveCurrentKa: string;
  typedPeakCurrentKa: string;
  typedSystemNominalVoltageV: string;
  typedFaultBusCode: string;
  incomer: BreakerDeviceDraft;
  outgoing: OutgoingDeviceDraft[];
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const breakerDeviceDraftShape = {
  code: z.string(),
  kind: chosenOrBlank(breakerKindSchema),
  ratedCurrentA: z.string(),
  ratedOperationalVoltageV: z.string(),
  ultimateBreakingCapacityKa: z.string(),
  serviceBreakingCapacityKa: z.string(),
  makingCapacityKa: z.string(),
  dutyBasis: chosenOrBlank(dutyBasisSchema),
};

const breakerDeviceDraftSchema = z.object(breakerDeviceDraftShape).strict();

const outgoingDeviceDraftSchema = z
  .object({
    id: z.string(),
    ...breakerDeviceDraftShape,
    selectivityRequired: chosenOrBlank(z.enum(SELECTIVITY_REQUIRED_CHOICES)),
    declaredSelectivityKind: chosenOrBlank(selectivityKindSchema),
    selectivityLimitKa: z.string(),
    cascadingLimitKa: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const breakerSelectivityDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    faultLevelSource: z.enum(FAULT_LEVEL_SOURCES),
    faultRunId: z.string(),
    typedProspectiveCurrentKa: z.string(),
    typedPeakCurrentKa: z.string(),
    typedSystemNominalVoltageV: z.string(),
    typedFaultBusCode: z.string(),
    incomer: breakerDeviceDraftSchema,
    outgoing: z.array(outgoingDeviceDraftSchema).min(1),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let nextRowNumber = 1;

// A prefix of its own, so an outgoing device id is never mistaken for another form's.
export function createOutgoingDeviceId(): string {
  const id = `breaker-${nextRowNumber}`;
  nextRowNumber += 1;
  return id;
}

// Row ids come from a counter that starts again at 1 on every page load, while a
// restored draft brings the ids it was saved with. Moving the counter past every id in use
// keeps a row added afterwards from taking the id of one already on screen.
export function seedOutgoingDeviceIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^breaker-(\d+)$/.exec(id);
    if (match !== null) {
      nextRowNumber = Math.max(nextRowNumber, Number(match[1]) + 1);
    }
  }
}

export function createBreakerDeviceDraft(): BreakerDeviceDraft {
  return {
    code: "",
    kind: "",
    ratedCurrentA: "",
    ratedOperationalVoltageV: "",
    ultimateBreakingCapacityKa: "",
    serviceBreakingCapacityKa: "",
    makingCapacityKa: "",
    dutyBasis: "",
  };
}

export function createOutgoingDeviceDraft(): OutgoingDeviceDraft {
  return {
    id: createOutgoingDeviceId(),
    ...createBreakerDeviceDraft(),
    selectivityRequired: "",
    declaredSelectivityKind: "",
    selectivityLimitKa: "",
    cascadingLimitKa: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile. The fault
// level starts on the linked run, as A21 (a) wants it, with no run chosen yet; the
// incomer and one empty outgoing device wait for the engineer; every choice starts
// blank.
export function createInitialBreakerSelectivityDraft(): BreakerSelectivityDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    faultLevelSource: "LINKED",
    faultRunId: "",
    typedProspectiveCurrentKa: "",
    typedPeakCurrentKa: "",
    typedSystemNominalVoltageV: "",
    typedFaultBusCode: "",
    incomer: createBreakerDeviceDraft(),
    outgoing: [createOutgoingDeviceDraft()],
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// One device as the request carries it. Required text and figures go as typed
// (trimmed, blank as "") so the schema names the blank by its label; an unmade
// choice is left out so the schema reports it as required; a blank Ics is left
// out so the schema applies the SERVICE rule.
function devicePayload(device: BreakerDeviceDraft): Record<string, unknown> {
  return {
    code: device.code.trim(),
    ...optional("kind", device.kind),
    rated_current_a: device.ratedCurrentA.trim(),
    rated_operational_voltage_v: device.ratedOperationalVoltageV.trim(),
    ultimate_breaking_capacity_ka: device.ultimateBreakingCapacityKa.trim(),
    making_capacity_ka: device.makingCapacityKa.trim(),
    ...optional("duty_basis", device.dutyBasis),
    ...optional("service_breaking_capacity_ka", device.serviceBreakingCapacityKa),
  };
}

// An outgoing device adds its declarations (A21 (c), (d)): whether selectivity is
// required as a boolean once chosen, the declared kind when chosen, and the limits
// only when typed.
function outgoingPayload(device: OutgoingDeviceDraft): Record<string, unknown> {
  const required =
    device.selectivityRequired === "yes"
      ? { selectivity_required: true }
      : device.selectivityRequired === "no"
        ? { selectivity_required: false }
        : {};
  return {
    ...devicePayload(device),
    ...required,
    ...optional("declared_selectivity_kind", device.declaredSelectivityKind),
    ...optional("selectivity_limit_ka", device.selectivityLimitKa),
    ...optional("cascading_limit_ka", device.cascadingLimitKa),
  };
}

// The fault level as chosen on screen: the linked run's id alone, or the typed
// block alone (A21 (a)). A blank run id or a blank typed figure is sent as "" so
// the schema names it, except the peak ip, which may be left out; the builder
// never sends both sources.
function faultLevelPayload(draft: BreakerSelectivityDraft): Record<string, unknown> {
  if (draft.faultLevelSource === "TYPED") {
    return {
      typed: {
        prospective_current_ka: draft.typedProspectiveCurrentKa.trim(),
        ...optional("peak_current_ka", draft.typedPeakCurrentKa),
        system_nominal_voltage_v: draft.typedSystemNominalVoltageV.trim(),
        fault_bus_code: draft.typedFaultBusCode.trim(),
      },
    };
  }
  return { fault_run_id: draft.faultRunId.trim() };
}

// The request body as the form will send it. It is validated afterwards with
// breakerSelectivityRunCreateRequestSchema, so this function does not judge the
// values and never reorders the outgoing devices.
export function buildBreakerSelectivityRunPayload(
  draft: BreakerSelectivityDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      ...faultLevelPayload(draft),
      incomer: devicePayload(draft.incomer),
      // Every device is sent, so the number in a message such as "Outgoing 2 — Device
      // code is required." always matches the row's position on screen.
      outgoing: draft.outgoing.map(outgoingPayload),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// The words of the device fields, shared by the incomer and the outgoing rows.
const DEVICE_LABELS = {
  code: "Device code",
  kind: "Breaker type",
  rated_current_a: "Rated current In (A)",
  rated_operational_voltage_v: "Rated operational voltage Ue (V)",
  ultimate_breaking_capacity_ka: "Ultimate breaking capacity Icu (kA)",
  service_breaking_capacity_ka: "Service breaking capacity Ics (kA)",
  making_capacity_ka: "Rated making capacity Icm (kA peak)",
  duty_basis: "Duty basis",
} as const;

function deviceLabels(prefix: string, group: string): Record<string, string> {
  return Object.fromEntries(
    Object.entries(DEVICE_LABELS).map(([field, label]) => [
      `${prefix}.${field}`,
      group === "" ? label : `${group} — ${label}`,
    ]),
  );
}

// On-screen labels of the breaker selectivity form, keyed by the request path.
// The run request nests the study under "study", so every study path carries
// that prefix; describeLocation numbers the outgoing rows, giving "Outgoing 2 — ...".
// The incomer is not a list, so its labels carry the group name themselves:
// "Incomer — Device code is required."
export const BREAKER_SELECTIVITY_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus code",
    "study.fault_run_id": "Fault run",
    "study.typed": "Fault level source",
    "study.typed.prospective_current_ka": "Prospective current Ik'' (kA)",
    "study.typed.peak_current_ka": "Peak current ip (kA)",
    "study.typed.system_nominal_voltage_v": "System nominal voltage (V)",
    "study.typed.fault_bus_code": "Fault bus code",
    "study.incomer": "Incomer",
    ...deviceLabels("study.incomer", "Incomer"),
    "study.outgoing": "Outgoing devices",
    ...deviceLabels("study.outgoing.*", ""),
    "study.outgoing.*.selectivity_required": "Selectivity with the incomer",
    "study.outgoing.*.declared_selectivity_kind": "Declared selectivity",
    "study.outgoing.*.selectivity_limit_ka": "Selectivity limit (kA)",
    "study.outgoing.*.cascading_limit_ka": "Cascading limit with the incomer (kA)",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
  // One row of the outgoing table: "Outgoing 2".
  groups: { incomer: "Incomer", outgoing: "Outgoing" },
};

// The words of every choice the form offers, typed against the contract enums
// so a value the contract gains cannot pass without a label.

export const BREAKER_KIND_LABELS: Record<BreakerKind, string> = {
  ACB: "Air circuit breaker (ACB)",
  MCCB: "Moulded-case circuit breaker (MCCB)",
  MCB: "Miniature circuit breaker (MCB)",
};

export const DUTY_BASIS_LABELS: Record<DutyBasis, string> = {
  ULTIMATE: "Ultimate (Icu)",
  SERVICE: "Service (Ics)",
};

export const SELECTIVITY_KIND_LABELS: Record<SelectivityKind, string> = {
  TOTAL: "Total",
  PARTIAL: "Partial (up to a limit)",
};

export const SELECTIVITY_REQUIRED_LABELS: Record<SelectivityRequiredChoice, string> = {
  yes: "Required",
  no: "Not required",
};
